// src/components/Interfaz.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as PetfamLogo } from "../assets/PetfamLogo.svg"; // ✅ SVG
import { Dog, Cat, ArrowUpRight, Pencil, Lock, Trash2, X, Eye, EyeOff, PlayCircle } from "lucide-react";
import "../styles/interfaz.css";
import PetfamCalendar from "./PetfamCalendar";
import { getBreedImage } from "../config/petBreedImages";
import VetAssistantChat from "./VetAssistantChat";
import petfamLogo from "../assets/Petfamp.png";
import config from "../config";
import { authFetch } from "../utils/authFetch";

import chipImg from "../assets/chip.webp";
import visa from "../assets/visa.webp";
import mc from "../assets/mastercard.webp";
import amex from "../assets/amex.webp";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

const IS_QA =
  (window?.location?.hostname || "").toLowerCase().includes("qa") ||
  (API_BASE_URL || "").toLowerCase().includes("qa");

// ✅ MODO PRUEBAS: NUNCA se bloquea la interfaz por plan
const PLAN_ACTIVE_FOR_TESTS = true;

// 💚 Recordatorios de salud
const HEALTH_REMINDERS = [
  "Tu mascota no puede decirte cuándo algo no está bien. La prevención permite detectar riesgos antes de que aparezcan los síntomas.",
  "El comportamiento es una forma de comunicación. Cambios en su conducta siempre tienen una causa. Aprende a interpretarlos con nuestro acompañamiento de veterinario conductal.",
  "La salud no se cuida solo cuando hay señales visibles. El seguimiento constante marca la diferencia. Descubre por qué la prevención importa.",
  "La ansiedad y el estrés no son mañas. Son respuestas emocionales que necesitan manejo adecuado. Agenda tu consulta con veterinario conductual.",
  "Pequeños cambios hoy pueden evitar grandes problemas mañana. Detectarlos a tiempo es clave. Activa tu seguimiento preventivo.",
  "Castigar no corrige el comportamiento, lo oculta. Entender la causa permite soluciones reales. Conoce un enfoque basado en ciencia.",
  "El bienestar emocional y el físico están conectados. Ignorar uno afecta el otro. Cuida ambos con PETFAM.",
  "Cada etapa de la vida requiere cuidados distintos. La edad también influye en la conducta. Acompaña cada etapa con expertos.",
  "Muchos problemas de comportamiento se construyen en silencio. La intervención temprana mejora la convivencia. Consulta con nuestro equipo especializado.",
  "Un control a tiempo puede evitar tratamientos largos y costosos. La prevención también es tranquilidad.",
  "El entorno influye más de lo que imaginas. Rutinas, espacio y cambios impactan el comportamiento. Aprende a crear un ambiente saludable.",
  "Cuidar a tu mascota es un proceso continuo, no un evento aislado. El acompañamiento hace la diferencia. Agenda una consulta con nuestros especialistas.",
];

function normalizeSpecies(v) {
  const s = String(v || "").trim().toLowerCase();

  if (s === "perro" || s === "dog" || s === "dogs" || s === "canino" || s === "canina") return "Perro";
  if (s === "gato" || s === "cat" || s === "cats" || s === "felino" || s === "felina") return "Gato";

  if (s === "perros") return "Perro";
  if (s === "gatos") return "Gato";

  return v || "";
}

function toTitleCaseName(name) {
  const s = String(name || "").trim();
  if (!s) return "";

  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function parseLocalDateParts(dateString) {
  if (!dateString) return null;

  const base = String(dateString).trim().slice(0, 10);
  const [year, month, day] = base.split("-").map(Number);

  if (!year || !month || !day) return null;

  return {
    year,
    month: month - 1,
    day,
  };
}


export default function Interfaz() {

  // ✅ Chat Dra. Vetia
  const [openVetChat, setOpenVetChat] = useState(false);
  const [chatPet, setChatPet] = useState(null);

  const [openDrPawsPicker, setOpenDrPawsPicker] = useState(false);
  const [drPawsMode, setDrPawsMode] = useState("CHAT"); // "CHAT" | "CONSULTA"

  const navigate = useNavigate();
  const location = useLocation();

  // 🔹 puntos laterales
  const [activeSpot, setActiveSpot] = useState("top");
  const topRef = useRef(null);
  const reminderRef = useRef(null);

  // ===== Header: user menu =====
  const [openMenu, setOpenMenu] = useState(false);
  const [openVideoGuide, setOpenVideoGuide] = useState(false);
  const [isMobileVideo, setIsMobileVideo] = useState(false);
  const menuRef = useRef(null);
  const toggleMenu = () => setOpenMenu((v) => !v);

  useEffect(() => {
  const checkDevice = () => {
    setIsMobileVideo(window.innerWidth <= 768);
  };

  checkDevice();
  window.addEventListener("resize", checkDevice);

  return () => window.removeEventListener("resize", checkDevice);
}, []);

useEffect(() => {
  const raw = localStorage.getItem("petfamUser");
  if (!raw) return;

  try {
    const user = JSON.parse(raw);
    const id = user?.idUser ?? user?.id ?? user?.userId;
    if (!id) return;

    const videoGuideSeenKey = `petfam_video_guide_seen_${id}`;
    const alreadySeen = localStorage.getItem(videoGuideSeenKey);

    if (!alreadySeen) {
      const timer = setTimeout(() => {
        setOpenVideoGuide(true);
        localStorage.setItem(videoGuideSeenKey, "true");
      }, 500);

      return () => clearTimeout(timer);
    }
  } catch {}
}, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpenMenu(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const topEl = topRef.current;
      const reminderEl = reminderRef.current;
      if (!topEl || !reminderEl) return;

      const centerY = window.innerHeight / 2;
      const topRect = topEl.getBoundingClientRect();
      const reminderRect = reminderEl.getBoundingClientRect();

      const distTop = Math.abs(topRect.top - centerY);
      const distReminder = Math.abs(reminderRect.top - centerY);

      if (distReminder < distTop) setActiveSpot("reminder");
      else setActiveSpot("top");
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ===== PERFIL DE USUARIO =====
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileLocation, setProfileLocation] = useState("");
  const [openProfilePanel, setOpenProfilePanel] = useState(false);
  const profileFileRef = useRef(null);
  const [userId, setUserId] = useState(null);

  const [country, setCountry] = useState("");

  // ===== ACTUALIZAR INFO DE PAGO =====
const [openPaymentModal, setOpenPaymentModal] = useState(false);
const [payLoading, setPayLoading] = useState(false);
const [payError, setPayError] = useState("");
const [paySuccess, setPaySuccess] = useState(false);

const [cardType, setCardType] = useState("visa"); // "visa" | "mc" | "amex"
const [cardHolder, setCardHolder] = useState("");
const [cardNumber, setCardNumber] = useState("");
const [cardExpMonth, setCardExpMonth] = useState("");
const [cardExpYear, setCardExpYear] = useState("");
const [cardCvc, setCardCvc] = useState("");

  // ===== CAMBIO DE CONTRASEÑA =====
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  // ===== MASCOTAS =====
  const [pets, setPets] = useState([]);
  const [filter, setFilter] = useState("todas");

  // ===== ELIMINAR MASCOTAS =====
  const [openDeletePetModal, setOpenDeletePetModal] = useState(false);
  const [petToDelete, setPetToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  // Modal límite de mascotas
  const [showPetLimitModal, setShowPetLimitModal] = useState(false);

  // 🔁 fuerza recarga del calendario
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  // Estados para edición manual
  const [editedName, setEditedName] = useState(profileName);
  const [editedPhone, setEditedPhone] = useState(profilePhone);
  const [editedLocation, setEditedLocation] = useState(profileLocation);

  const [savingProfile, setSavingProfile] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const readUserFromStorage = () => {
    try {
      const raw = localStorage.getItem("petfamUser");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const resolveUserId = (u) => {
    const id = u?.idUser ?? u?.id ?? u?.userId ?? null;
    return id != null ? Number(id) : null;
  };

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Todos los campos son obligatorios.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("La confirmación no coincide con la nueva contraseña.");
      return;
    }

    if (!userId) {
      setPasswordError("No se pudo identificar al usuario.");
      return;
    }

    try {
      setPasswordLoading(true);

      const res = await authFetch(`/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idUser: userId,
          currentPassword,
          newPassword,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json || json.status !== 200) {
        setPasswordError(
          json?.userMessage || "No se pudo cambiar la contraseña. Verifica tu contraseña actual."
        );
        return;
      }

      setPasswordSuccess("Tu contraseña se actualizó correctamente.");
      resetPasswordForm();

      setTimeout(() => {
        setOpenPasswordModal(false);
        setPasswordSuccess("");
      }, 1200);
    } catch {
      setPasswordError("Ocurrió un error al conectar con el servidor. Inténtalo de nuevo.");
    } finally {
      setPasswordLoading(false);
    }
  };

  // ====== CARGAR MASCOTAS ======
  const loadPets = async (idUser) => {
    try {
      const res = await authFetch(`/pets/user/${idUser}`);
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        const list = json.data.map((p) => ({
          id: p.idPet,
          idUser: idUser,
          nombre: p.name,
          tipo: normalizeSpecies(p.species),
          raza: p.breed,
          sexo: p.sex,
          peso: p.weightKg ? `${p.weightKg} kg` : "",
          nacimiento: p.birthDate || "",
          foto: p.cardImageUrl,
          profilePhotoUrl: p.profilePhotoUrl || null,
        }));

        setPets(list);
      } else {
        setPets([]);
      }
    } catch (err) {
      console.error("Error cargando mascotas del usuario", err);
    }
  };

  // ====== SINCRONIZAR USER (SIN PLAN) ======
  const syncUserFromStorage = async () => {
    const local = readUserFromStorage();
    if (!local) return;

    const initialId = resolveUserId(local);
    if (!initialId) return;

    const applyUserToState = (u) => {
      const resolved = resolveUserId(u) ?? initialId;
      setUserId(resolved);

      const nm = u?.name ?? "";
      setProfileName(nm);
      setEditedName(nm);

      const ph = u?.phone ?? "";
      setProfilePhone(ph);
      setEditedPhone(ph);

      const ct = u?.country ?? "";
      setCountry(ct);
      setProfileLocation(ct);
      setEditedLocation(ct);

      if (u?.profilePhotoUrl) {
        const url = u.profilePhotoUrl.startsWith("http") ? u.profilePhotoUrl : `${API_BASE_URL}${u.profilePhotoUrl}`;
        setProfilePhoto(url);
      } else {
        setProfilePhoto(null);
      }

      return resolved;
    };

    const localId = applyUserToState(local);

    try {
      const res = await authFetch(`/user/${initialId}`);
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        const dbUser = json.data;

        const merged = {
          ...local,
          ...dbUser,
          email: local.email ?? dbUser.email,
          roles: local.roles ?? [],
        };

        localStorage.setItem("petfamUser", JSON.stringify(merged));
        const realId = applyUserToState(merged);

        await loadPets(realId);
        return;
      }

      await loadPets(localId);
    } catch {
      await loadPets(localId);
    }
  };

  useEffect(() => {
    syncUserFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onFocus = () => syncUserFromStorage();
    const onVisibility = () => {
      if (document.visibilityState === "visible") syncUserFromStorage();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Foto perfil =====
  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setProfilePhoto(previewUrl);

    const raw = localStorage.getItem("petfamUser");
    if (!raw) return;
    const user = JSON.parse(raw);

    const id = resolveUserId(user);
    if (!id) {
      console.warn("⚠ Usuario no tiene idUser en el almacenamiento");
      return;
    }

    const formData = new FormData();
    formData.append("photo", file);

    try {
      const res = await authFetch(`/user/${id}/photo`, {
  method: "POST",
  body: formData,
});

      const json = await res.json().catch(() => null);
      if (!json?.data?.profilePhotoUrl) {
        console.error("⚠ Backend no devolvió profilePhotoUrl:", json);
        return;
      }

      const serverUrl = json.data.profilePhotoUrl.startsWith("http")
        ? json.data.profilePhotoUrl
        : `${API_BASE_URL}${json.data.profilePhotoUrl}`;

      setProfilePhoto(serverUrl);

      const updatedUser = {
        ...user,
        ...json.data,
        idUser: json.data.idUser || id,
        profilePhotoUrl: json.data.profilePhotoUrl,
      };

      localStorage.setItem("petfamUser", JSON.stringify(updatedUser));
    } catch (err) {
      console.error("❌ Error subiendo foto:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (profilePhoto && profilePhoto.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(profilePhoto);
        } catch {}
      }
    };
  }, [profilePhoto]);

  // ===== Filtro mascotas =====
  const filteredPets = pets.filter((pet) => {
    if (filter === "todas") return true;
    if (filter === "perros") return pet.tipo === "Perro";
    if (filter === "gatos") return pet.tipo === "Gato";
    return true;
  });

  const scrollToCalendar = () => {
    const section = document.getElementById("calendar-section");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openPet = (pet) => {
    const normalizedPet = {
      idPet: pet.id || pet.idPet || null,
      idUser: pet.idUser ?? userId ?? null,
      nombre: pet.nombre,
      tipo: pet.tipo,
      raza: pet.raza,
      sexo: pet.sexo,
      peso: pet.peso,
      nacimiento: pet.nacimiento,
      profilePhotoUrl: pet.profilePhotoUrl,
      foto: pet.foto,
    };

    navigate("/informascota", {
      state: { pet: normalizedPet, swipeIn: "up", swipeOut: "down" },
    });
  };

  const handleDateSelect = (date) => {
    console.log("Fecha seleccionada:", date);
  };

  const birthdayEvents = pets.flatMap((p) => {
  if (!p.nacimiento) return [];

  const parsed = parseLocalDateParts(p.nacimiento);
  if (!parsed) return [];

  return [
    {
      id: `birthday-${p.id}`,
      idPet: p.id,
      month: parsed.month,
      day: parsed.day,
      title: `Cumpleaños de ${p.nombre}`,
      type: "birthday",
      petName: p.nombre,
    },
  ];
});

  const getDayNumber = () => Math.floor(new Date().getTime() / (1000 * 60 * 60 * 24));
  const reminderIndex = getDayNumber() % HEALTH_REMINDERS.length;
  const todayReminder = HEALTH_REMINDERS[reminderIndex];

  const isHomeActive = location.pathname === "/interfaz" || location.pathname === "/";
  const isTuPlanActive = location.pathname === "/tu-plan";

  // ✅ YA NO bloquea por plan
  const handleAddPetClick = () => {
    if (pets.length >= 3) setShowPetLimitModal(true);
    else navigate("/anadirmascota", { state: { transition: "panel" } });
  };

  const handleRemovePetClick = () => {
    setDeleteError("");
    setOpenDeletePetModal(true);
  };

  const askConfirmDelete = (pet) => {
    setDeleteError("");
    setPetToDelete(pet);
    setOpenConfirmDelete(true);
  };

  const doDeletePet = async () => {
    if (!petToDelete?.id) return;

    setDeleteLoading(true);
    setDeleteError("");

    try {
      const res = await authFetch(`/pets/${petToDelete.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("No se pudo eliminar la mascota");

      setPets((prev) => prev.filter((p) => p.id !== petToDelete.id));
      if (userId) await loadPets(userId);

      setOpenConfirmDelete(false);
      setOpenDeletePetModal(false);
      setPetToDelete(null);

      setCalendarRefreshKey((k) => k + 1);
    } catch {
      setDeleteError("No se pudo eliminar la mascota. Verifica el backend o el endpoint DELETE.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAcceptExtraPet = () => {
    setShowPetLimitModal(false);
    navigate("/anadirmascota", { state: { transition: "panel", extraPet: true } });
  };

  // ✅ Dr. Paws (sin bloqueo)
  const openDrPawsForPet = (pet, mode = "CHAT") => {
    if (!pet) return;

    const normalizedPet = {
      idPet: pet.idPet ?? pet.id ?? pet.id_pet ?? null,
      name: pet.name ?? pet.nombre,
      nombre: pet.nombre ?? pet.name,
      species: pet.species ?? pet.tipo,
      birthDate: pet.birthDate ?? pet.nacimiento,
      idUser: pet.idUser ?? userId ?? null,
      mode,
    };

    setChatPet(normalizedPet);
    setOpenVetChat(true);
  };

  const handleOpenDrPaws = (petId) => {
    if (!pets || pets.length === 0) {
      alert("Primero crea una mascota para que laa Dra.Vetia pueda ayudarte");
      return;
    }

    if (petId != null) {
      const found = (pets || []).find((p) => String(p.id) === String(petId));
      if (found) {
        openDrPawsForPet(found, "CHAT");
        return;
      }
    }

    if (pets.length === 1) {
      openDrPawsForPet(pets[0], "CHAT");
      return;
    }

    setDrPawsMode("CHAT");
    setOpenDrPawsPicker(true);
  };

  const handlePickPetForDrPaws = (pet) => {
    setOpenDrPawsPicker(false);
    openDrPawsForPet(pet, drPawsMode);
  };
  const onlyDigits = (s) => String(s || "").replace(/\D/g, "");
const formatCardNumber = (value) => {
  const d = onlyDigits(value).slice(0, 16);
  return d.replace(/(\d{4})(?=\d)/g, "$1 ");
};

function normalizeCardHolder(value) {
  return String(value || "")
    .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trimStart();
}

// (Opcional) si quieres que *todo texto* se vuelva mayúscula automáticamente:
function toUpperText(value) {
  return String(value || "").toUpperCase();
}

const wompiTokenizeEndpoint = () => {
  // ✅ QA -> sandbox, PROD -> production
  return IS_QA ? "https://sandbox.wompi.co/v1/tokens/cards" : "https://production.wompi.co/v1/tokens/cards";
};

const getUserFromStorage = () => {
  try {
    const raw = localStorage.getItem("petfamUser");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const handleSavePaymentSource = async () => {
  try {
    setPayLoading(true);
    setPayError("");
    setPaySuccess(false);

    const u = getUserFromStorage();
    const email = (u?.email || "").toString().trim().toLowerCase();
    const id = resolveUserId(u);

    if (!id) throw new Error("No se pudo identificar el usuario (idUser).");
    if (!email) throw new Error("No encontré el email del usuario.");

    // 1) merchant (publicKey + tokens)
    const mRes = await authFetch(`/wompi/merchant`);
    const merchant = await mRes.json().catch(() => null);
    if (!mRes.ok || !merchant?.publicKey) {
      throw new Error(merchant?.message || "No se pudo obtener merchant de Wompi.");
    }

    const publicKey = merchant.publicKey;
    const acceptanceToken = merchant?.acceptance?.token;
    const personalAuthToken = merchant?.personalData?.token;

    if (!acceptanceToken || !personalAuthToken) {
      throw new Error("Wompi merchant no devolvió tokens de aceptación.");
    }

    // 2) Tokenizar tarjeta (tok_...)
    const payloadToken = {
      number: onlyDigits(cardNumber),
      cvc: onlyDigits(cardCvc),
      exp_month: onlyDigits(cardExpMonth),
      exp_year: onlyDigits(cardExpYear),
      card_holder: String(cardHolder || "").trim(),
    };

    if (!payloadToken.card_holder) throw new Error("El nombre del titular es obligatorio.");
    if (payloadToken.number.length < 13) throw new Error("Número de tarjeta inválido.");
    if (payloadToken.cvc.length < 3) throw new Error("CVC inválido.");
    if (payloadToken.exp_month.length < 1 || Number(payloadToken.exp_month) < 1 || Number(payloadToken.exp_month) > 12) {
      throw new Error("Mes de expiración inválido.");
    }
    if (payloadToken.exp_year.length < 2) throw new Error("Año de expiración inválido.");

    const tRes = await fetch(wompiTokenizeEndpoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicKey}`,
      },
      body: JSON.stringify(payloadToken),
    });

    const tJson = await tRes.json().catch(() => null);
    const token = tJson?.data?.id;

    if (!tRes.ok || !token) {
      const msg = tJson?.error?.messages ? JSON.stringify(tJson.error.messages) : (tJson?.error?.reason || tJson?.message || "Wompi rechazó la tokenización.");
      throw new Error(msg);
    }

    // 3) Crear Payment Source en backend (usa tus tokens si quieres enviarlos, o dejas que backend los obtenga)
    const psRes = await authFetch(`/wompi/payment-sources/card`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        customerEmail: email,

        // ✅ opcional: si los mandas, evitas un fetch extra en backend
        acceptanceToken,
        acceptPersonalAuthToken: personalAuthToken,
      }),
    });

    const psJson = await psRes.json().catch(() => null);
    if (!psRes.ok || !psJson?.ok) {
      throw new Error(psJson?.message || "No se pudo crear la fuente de pago.");
    }

    const paymentSourceId = psJson?.data?.paymentSourceId;
    if (!paymentSourceId) throw new Error("Wompi no devolvió paymentSourceId.");

    // 4) Guardar paymentSourceId en tu usuario
    const upRes = await authFetch(`/user/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paymentSourceId }),
    });

    const upJson = await upRes.json().catch(() => null);
    if (!upRes.ok || upJson?.status !== 200) {
      throw new Error(upJson?.userMessage || "No se pudo guardar la fuente de pago en el usuario.");
    }

    // 5) Persistir en localStorage (para que se refleje sin recargar)
    const updatedUser = {
      ...u,
      paymentSourceId,
    };
    localStorage.setItem("petfamUser", JSON.stringify(updatedUser));

    setPaySuccess(true);
    setTimeout(() => setPaySuccess(false), 1500);

    // limpiar campos sensibles (no guardar tarjeta en memoria)
    setCardHolder("");
    setCardNumber("");
    setCardExpMonth("");
    setCardExpYear("");
    setCardCvc("");

    // cerrar modal
    setTimeout(() => setOpenPaymentModal(false), 600);
  } catch (e) {
    setPayError(e?.message || "Error actualizando información de pago.");
  } finally {
    setPayLoading(false);
  }
};

  return (
    <>
      <div className="page-enter">
        {/* PUNTOS LATERALES */}
        <div className="scroll-dots">
          <button
            type="button"
            className={`dot ${activeSpot === "top" ? "active" : ""}`}
            onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            aria-label="Ir a tus mascotas"
          />
          <button
            type="button"
            className={`dot ${activeSpot === "reminder" ? "active" : ""}`}
            onClick={() => reminderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
            aria-label="Ir al recordatorio de salud"
          />
        </div>

        {/* HEADER */}
        <header className="header">
          <div className="header-container">
            <div className="nav-left">
              <PetfamLogo className="logo" aria-label="Logo Petfam" />
            </div>

            <nav className="nav-center">
              <Link to="/interfaz" state={{ noAnim: true }} className={`${isHomeActive ? "active" : ""} underline-anim`}>
                Inicio
              </Link>
              <Link to="/tu-plan" className={`${isTuPlanActive ? "active" : ""} underline-anim`}>
                Tu plan
              </Link>
            </nav>

            <div className="nav-right">
              <div className="user-menu" ref={menuRef}>
                <button
                  className="icon-user"
                  onClick={toggleMenu}
                  aria-label="Perfil"
                  aria-haspopup="menu"
                  aria-expanded={openMenu}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="user-svg">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="arrow-svg">
                    <path fillRule="evenodd" d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z" clipRule="evenodd" />
                  </svg>
                </button>

                {openMenu && (
                  <div className="dropdown-menu" role="menu">
                    <Link
                      to={location.pathname}
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenMenu(false);
                        setOpenProfilePanel(true);
                      }}
                    >
                      <Pencil size={18} style={{ marginRight: 10 }} strokeWidth={1.8} />
                      Editar perfil
                    </Link>

                     <button
      type="button"
      role="menuitem"
      className="dropdown-menu-btn"
      onClick={() => {
        setOpenMenu(false);
        setOpenVideoGuide(true);
      }}
    >
      <PlayCircle size={18} style={{ marginRight: 10 }} strokeWidth={1.8} />
      Video guiado
    </button>

                    <hr />

                    <Link
                      to="/"
                      state={{ from: "interfaz" }}
                      role="menuitem"
                      onClick={() => {
                        localStorage.removeItem("petfamUser");
                        localStorage.removeItem("idUser");
                        localStorage.removeItem("token");
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="20" height="20" style={{ marginRight: 10 }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6A2.25 2.25 0 0 0 5.25 5.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                      </svg>
                      Salir
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* HERO */}
        <section className="herointerfaz">
          <div className="hero-wave">
            <svg viewBox="0 0 1440 160" preserveAspectRatio="none" className="curve-svg">
              <path
                d="M0,0 L1440,0 L1440,160
                 C1200,130 960,110 720,120
                 C480,130 240,150 0,120 Z"
                fill="#F8E6C6"
              />
            </svg>
          </div>

          <div className="hero-inner">
            <div className="greet-row">
              <div className="greet-block">
                <label className={`avatar ${profilePhoto ? "has-photo" : ""}`}>
                  <input type="file" accept="image/*" onChange={handleProfilePhotoChange} />
                  {profilePhoto ? (
                    <>
                      <span className="avatar-img" style={{ backgroundImage: `url(${profilePhoto})` }} />
                      <span className="avatar-grad" aria-hidden="true" />
                    </>
                  ) : (
                    <>
                      <span className="avatar-placeholder">+</span>
                      <span className="avatar-msg">Añadir tu foto</span>
                    </>
                  )}
                </label>

                <div className="greet-col">
                  <h2 className="saludo">
  {profileName ? `¡Hola, ${toTitleCaseName(profileName)}!` : "¡Hola!"}
</h2>

                  <div className="country-row">
                    <span className="pin" aria-hidden>
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          fill="currentColor"
                          d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z"
                        />
                      </svg>
                    </span>

                    <span className="country-text">{profileLocation || country || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECCIÓN MASCOTAS + CALENDARIO */}
        <section className="pets-section" aria-labelledby="pets-title" ref={topRef}>
          <div className="pets-calendar-layout">
            {/* COLUMNA IZQUIERDA */}
            <div className="left-pets-column">
              <div className="pets-toolbar">
                <div className="pets-toolbar-left">
                  <button className="add-pet-btn" type="button" onClick={handleAddPetClick}>
                    <span className="add-icon" aria-hidden>＋</span>
                    Mascota
                  </button>

                  <button
                    className="add-pet-btn remove-pet-btn"
                    type="button"
                    onClick={handleRemovePetClick}
                    disabled={pets.length === 0}
                    title={pets.length === 0 ? "No tienes mascotas para eliminar" : "Eliminar mascota"}
                  >
                    <span className="add-icon" aria-hidden>－</span>
                    Mascota
                  </button>

                  <button type="button" className="calendar-scroll-btn" onClick={scrollToCalendar}>
                    Calendario
                  </button>
                </div>

                <div className="pets-heading">
                  <h2 id="pets-title" className="pets-title">Tus mascotas</h2>
                  <p className="pets-helper" role="note">Selecciona una mascota para programar una cita.</p>
                </div>

                <div className="toolbar-spacer" />
              </div>

              {/* Filtros */}
              <div className="pet-filters" role="tablist" aria-label="Filtrar mascotas">
                <button
                  role="tab"
                  aria-selected={filter === "todas"}
                  className={`chip ${filter === "todas" ? "is-active" : ""}`}
                  onClick={() => setFilter("todas")}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                    <path
                      fill="currentColor"
                      d="M12 13c-2.2 0-4 1.8-4 4 0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2 0-2.2-1.8-4-4-4Zm-5.1-1.2c.9.4 2-.1 2.4-1 .4-.9-.1-2-1-2.4s-2 .1-2.4 1 .1 2 1 2.4Zm10.2 0c.9-.4 1.4-1.5 1-2.4s-1.5-1.4-2.4-1-1.4 1.5-1 2.4 1.5 1.4 2.4 1Zm-6.7-2.9c1 0 1.8-.8 1.8-1.8S11.4 3.3 10.4 3.3 8.6 4.1 8.6 5.1 9.4 8.9 10.4 8.9Zm3.2 0c1 0 1.8-.8 1.8-1.8s-.8-1.8-1.8-1.8-1.8.8-1.8 1.8.8 1.8 1.8 1.8Z"
                    />
                  </svg>
                  <span>Todos</span>
                </button>

                <button
                  role="tab"
                  aria-selected={filter === "perros"}
                  className={`chip ${filter === "perros" ? "is-active" : ""}`}
                  onClick={() => setFilter("perros")}
                >
                  <Dog size={18} strokeWidth={2} />
                  <span>Perros</span>
                </button>

                <button
                  role="tab"
                  aria-selected={filter === "gatos"}
                  className={`chip ${filter === "gatos" ? "is-active" : ""}`}
                  onClick={() => setFilter("gatos")}
                >
                  <Cat size={18} strokeWidth={2} />
                  <span>Gatos</span>
                </button>
              </div>

              {/* Tarjetas */}
              <div className="pets-grid">
                {filteredPets.map((pet) => (
                  <div
                    key={pet.id}
                    className="pet-cards pet-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => openPet(pet)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPet(pet);
                      }
                    }}
                    style={{
                      "--img-w": pet.tipo === "Gato" ? "175px" : "200px",
                      "--img-left": pet.tipo === "Gato" ? "-4px" : "-35px",
                      "--img-bottom": "0px",
                      "--img-scale": "1",
                    }}
                  >
                    <div className="pet-image-frame">
                      <img
                        src={pet.foto || getBreedImage(pet.tipo, pet.raza)}
                        alt={pet.nombre}
                        className="pet-img"
                      />
                    </div>

                    <div className="pet-info">
                      <h3>{pet.nombre}</h3>
                      <p>{pet.raza}</p>
                    </div>

                    <button className="pet-arrow" aria-hidden="true" tabIndex={-1}>
                      <ArrowUpRight size={18} strokeWidth={2.4} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* COLUMNA DERECHA: calendario (SIEMPRE ACTIVO) */}
            <div className="right-calendar-column" id="calendar-section">
              <h2 className="calendar-title">Calendario</h2>
              <PetfamCalendar
                idUser={userId}
                onDateSelect={handleDateSelect}
                birthdayEvents={birthdayEvents}
                pets={pets}
                refreshKey={calendarRefreshKey}
                onOpenDrPaws={handleOpenDrPaws}
                planActive={true}
              />
            </div>
          </div>
        </section>

        {/* 💚 MENSAJE DEL DÍA */}
        <section className="health-reminder-shell" ref={reminderRef}>
          <div className="health-reminder-card">
            <h3 className="health-reminder-title">Mensaje del día</h3>
            <p className="health-reminder-text">{todayReminder}</p>
          </div>
        </section>
      </div>

      {/* ===== PANEL EDITA TU PERFIL ===== */}
      {openProfilePanel && (
        <div
          className="profile-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenProfilePanel(false);
          }}
        >
          <div className="profile-panel" onClick={(e) => e.stopPropagation()}>
            <button className="profile-close" onClick={() => setOpenProfilePanel(false)} aria-label="Cerrar edición de perfil">
              <X size={18} />
            </button>

            <h2 className="profile-title">Edita tu perfil</h2>

            <div className="profile-top">
              <div className="profile-photo-edit">
                <input ref={profileFileRef} type="file" accept="image/*" onChange={handleProfilePhotoChange} className="sr-only-input" />

                <button
                  type="button"
                  className={`profile-photo-box ${profilePhoto ? "has-photo" : ""}`}
                  onClick={() => profileFileRef.current?.click()}
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Foto de perfil" />
                  ) : (
                    <>
                      <span className="profile-photo-plus">+</span>
                      <span className="profile-photo-text">Editar foto</span>
                    </>
                  )}
                </button>
              </div>

              <div className="profile-fields">
                <label className="profile-label">
  Nombre
  <input
    type="text"
    value={profileName}
    onChange={(e) => {
      const v = (e.target.value || "").toUpperCase();
      setProfileName(v);
      setEditedName(v);
    }}
    autoCapitalize="characters"
    spellCheck={false}
  />
</label>

                <label className="profile-label">
                  Celular
                  <input
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => {
                      setProfilePhone(e.target.value);
                      setEditedPhone(e.target.value);
                    }}
                  />
                </label>

                <label className="profile-label">
                  Ubicación
                  <input
                    type="text"
                    value={profileLocation}
                    onChange={(e) => {
                      const v = e.target.value;
                      setProfileLocation(v);
                      setEditedLocation(v);
                      setCountry(v);
                    }}
                  />
                </label>

                <div className="profile-save-box" style={{ marginTop: "10px" }}>
                  {saveSuccess && <p className="save-success">✔ Cambios guardados</p>}
                  {saveError && <p className="save-error">⚠ {saveError}</p>}

                  <button
                    type="button"
                    className="profile-save-btn"
                    onClick={async () => {
                      setSavingProfile(true);
                      setSaveError("");
                      setSaveSuccess(false);

                      try {
                        const raw = localStorage.getItem("petfamUser");
                        if (!raw) throw new Error("No se encontró usuario en localStorage");
                        const user = JSON.parse(raw);

                        const id = resolveUserId(user);
                        if (!id) throw new Error("No se encontró idUser");

                        const res = await authFetch(`/user/${id}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            name: editedName,
                            phone: editedPhone,
                            country: editedLocation,
                          }),
                        });

                        const json = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(json?.userMessage || "Error guardando en backend");

                        const updatedUser = {
                          ...user,
                          ...(json?.data || {}),
                          name: editedName,
                          phone: editedPhone,
                          country: editedLocation,
                          location: editedLocation,
                        };
                        localStorage.setItem("petfamUser", JSON.stringify(updatedUser));

                        setProfileName(editedName);
                        setProfilePhone(editedPhone);
                        setProfileLocation(editedLocation);

                        setSaveSuccess(true);
                        setTimeout(() => setSaveSuccess(false), 1500);
                      } catch {
                        setSaveError("No se pudieron guardar los cambios");
                      } finally {
                        setSavingProfile(false);
                      }
                    }}
                    disabled={savingProfile}
                  >
                    {savingProfile ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>
            </div>

            <div className="profile-bottom">
              <button
                type="button"
                className="profile-link"
                onClick={() => {
                  resetPasswordForm();
                  setOpenProfilePanel(false);
                  setOpenPasswordModal(true);
                }}
              >
                <Lock size={16} />
                <span>Editar contraseña</span>
              </button>
              <button
  type="button"
  className="profile-link"
  onClick={() => {
    setPayError("");
    setPaySuccess(false);
    setOpenPaymentModal(true);
  }}
>
  <span style={{ fontSize: 16 }}>💳</span>
  <span>Actualizar información de pago</span>
</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL CAMBIAR CONTRASEÑA ===== */}
      {openPasswordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal pro">
            <button
              type="button"
              className="password-close"
              onClick={() => {
                resetPasswordForm();
                setOpenPasswordModal(false);
              }}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>

            <div className="password-header">
              <div className="password-logo">
                <img src={petfamLogo} alt="Petfam" />
              </div>

              <h3>Cambiar contraseña</h3>
              <p>
                Primero valida tu contraseña actual.
                <br />
                Luego podrás definir una nueva.
              </p>
            </div>

            {passwordError && <p className="password-message password-error">{passwordError}</p>}
            {passwordSuccess && <p className="password-message password-success">{passwordSuccess}</p>}

            <form onSubmit={handlePasswordSubmit} className="password-form">
              <label>
  Contraseña actual

  <div className="pw-field">
    <input
      type={showCurrentPassword ? "text" : "password"}
      value={currentPassword}
      onChange={(e) => setCurrentPassword(e.target.value)}
      placeholder="••••••••"
      autoFocus
    />

    <button
      type="button"
      className="pw-toggle"
      onClick={() => setShowCurrentPassword((v) => !v)}
      aria-label={showCurrentPassword ? "Ocultar contraseña actual" : "Mostrar contraseña actual"}
      title={showCurrentPassword ? "Ocultar" : "Mostrar"}
    >
      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</label>

<label>
  Nueva contraseña

  <div className="pw-field">
    <input
      type={showNewPassword ? "text" : "password"}
      value={newPassword}
      onChange={(e) => setNewPassword(e.target.value)}
      placeholder="Mínimo 8 caracteres"
    />

    <button
      type="button"
      className="pw-toggle"
      onClick={() => setShowNewPassword((v) => !v)}
      aria-label={showNewPassword ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
      title={showNewPassword ? "Ocultar" : "Mostrar"}
    >
      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</label>

<label>
  Confirmar nueva contraseña

  <div className="pw-field">
    <input
      type={showConfirmPassword ? "text" : "password"}
      value={confirmPassword}
      onChange={(e) => setConfirmPassword(e.target.value)}
      placeholder="Repite la nueva contraseña"
    />

    <button
      type="button"
      className="pw-toggle"
      onClick={() => setShowConfirmPassword((v) => !v)}
      aria-label={showConfirmPassword ? "Ocultar confirmación" : "Mostrar confirmación"}
      title={showConfirmPassword ? "Ocultar" : "Mostrar"}
    >
      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</label>


              <div className="password-actions">
                <button
                  type="button"
                  className="password-cancel"
                  onClick={() => {
                    resetPasswordForm();
                    setOpenPasswordModal(false);
                  }}
                  disabled={passwordLoading}
                >
                  Cancelar
                </button>

                <button type="submit" className="password-submit" disabled={passwordLoading}>
                  {passwordLoading ? "Guardando..." : "Guardar contraseña"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== MODAL: ELEGIR MASCOTA A ELIMINAR ===== */}
      {openDeletePetModal && (
        <div
          className="password-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpenDeletePetModal(false);
              setOpenConfirmDelete(false);
              setPetToDelete(null);
              setDeleteError("");
            }
          }}
        >
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="password-close"
              onClick={() => {
                setOpenDeletePetModal(false);
                setOpenConfirmDelete(false);
                setPetToDelete(null);
                setDeleteError("");
              }}
              aria-label="Cerrar eliminación de mascota"
            >
              <X size={18} />
            </button>

            <h3 className="password-title">¿A qué mascota quieres eliminar?</h3>

            {deleteError && <p className="password-message password-error">{deleteError}</p>}

            {pets.length === 0 ? (
              <p className="password-helper">Aún no tienes mascotas registradas.</p>
            ) : (
              <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                {pets.map((pet) => {
                  const avatarSrc = pet.foto || getBreedImage(pet.tipo, pet.raza);

                  return (
                    <div
                      key={pet.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(0,0,0,0.06)",
                        background: "rgba(255,255,255,0.65)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            overflow: "hidden",
                            flex: "0 0 44px",
                            background: "#fff",
                            border: "1px solid rgba(0,0,0,0.08)",
                          }}
                        >
                          <img src={avatarSrc} alt={pet.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>

                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <strong style={{ lineHeight: 1.1 }}>{pet.nombre}</strong>
                          <span style={{ fontSize: 12, opacity: 0.75 }}>
                            {pet.tipo} {pet.raza ? `· ${pet.raza}` : ""}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => askConfirmDelete(pet)}
                        aria-label={`Eliminar a ${pet.nombre}`}
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 12,
                          border: "1px solid rgba(0,0,0,0.08)",
                          background: "white",
                          display: "grid",
                          placeItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="password-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="password-cancel"
                onClick={() => {
                  setOpenDeletePetModal(false);
                  setOpenConfirmDelete(false);
                  setPetToDelete(null);
                  setDeleteError("");
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: CONFIRMAR ELIMINACIÓN ===== */}
      {openConfirmDelete && petToDelete && (
        <div
          className="password-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) setOpenConfirmDelete(false);
          }}
        >
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="password-close"
              onClick={() => !deleteLoading && setOpenConfirmDelete(false)}
              aria-label="Cerrar confirmación"
            >
              <X size={18} />
            </button>

            <h3 className="password-title">Confirmar eliminación</h3>
            <p className="password-helper">
              ¿Estás seguro de eliminar a <strong>{petToDelete.nombre}</strong>?
            </p>

            {deleteError && <p className="password-message password-error">{deleteError}</p>}

            <div className="password-actions">
              <button type="button" className="password-cancel" onClick={() => setOpenConfirmDelete(false)} disabled={deleteLoading}>
                No
              </button>

              <button type="button" className="password-submit" onClick={doDeletePet} disabled={deleteLoading}>
                {deleteLoading ? "Eliminando..." : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL LÍMITE DE MASCOTAS ===== */}
      {showPetLimitModal && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <button type="button" className="password-close" onClick={() => setShowPetLimitModal(false)} aria-label="Cerrar aviso de límite de mascotas">
              <X size={18} />
            </button>

            <h3 className="password-title">Has alcanzado el límite de mascotas</h3>
            <p className="password-helper">
              Puedes tener hasta <strong>3 mascotas</strong> incluidas en tu plan.
            </p>
          </div>
        </div>
      )}

      {/* ✅ Modal: elegir mascota para Dr.Paws */}
      {openDrPawsPicker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}
          onClick={() => setOpenDrPawsPicker(false)}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              background: "#fff8f0",
              borderRadius: 18,
              padding: 16,
              boxShadow: "0 18px 40px rgba(0,0,0,.18)",
              border: "1px solid rgba(0,0,0,.06)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: 0, color: "#0b2f3e" }}>¿Para qué mascota quieres hablar con la Dra.Vetia?</h4>
            <p style={{ marginTop: 6, marginBottom: 14, fontSize: 13, opacity: 0.75 }}>
              Selecciona una de tus mascotas registradas.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {pets?.map((p) => (
                <button
                  key={p.id ?? p.idPet ?? p.id_pet}
                  type="button"
                  onClick={() => handlePickPetForDrPaws(p)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,.08)",
                    background: "#ffffff",
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#0b2f3e",
                  }}
                >
                  <span>{p.nombre ?? p.name ?? "Mascota"}</span>
                  <span style={{ fontSize: 12, opacity: 0.7 }}>
                    {(p.tipo ?? p.species ?? "").toString().toLowerCase()}
                  </span>
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setOpenDrPawsPicker(false)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "none",
                  background: "#f2deca",
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#0b2f3e",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Chat Dr.Paws */}
      {openVetChat && chatPet && (
        <VetAssistantChat
          pet={chatPet}
          onClose={() => setOpenVetChat(false)}
          onVaccinesSaved={() => setCalendarRefreshKey((k) => k + 1)}
        />
      )}
      {/* ===== MODAL: ACTUALIZAR INFO DE PAGO ===== */}
{openPaymentModal && (
  <div
    className="pay-modal-overlay"
    onClick={(e) => {
      if (e.target === e.currentTarget && !payLoading) setOpenPaymentModal(false);
    }}
  >
    <div className="pay-modal" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="pay-close"
        onClick={() => !payLoading && setOpenPaymentModal(false)}
        aria-label="Cerrar"
      >
        <X size={18} />
      </button>

      <h3 className="pay-title">Actualizar información de pago</h3>

      <p className="pay-sub">
        <span className="pay-sub2">Por seguridad, no almacenamos el número de tu tarjeta.</span>
      </p>

      {/* ✅ selector tipo tarjeta */}
      <div className="pay-types" role="tablist" aria-label="Tipo de tarjeta">
        <button
          type="button"
          className={`pay-type-btn ${cardType === "visa" ? "active" : ""}`}
          onClick={() => setCardType("visa")}
          disabled={payLoading}
          aria-label="Visa"
        >
          <img src={visa} alt="Visa" loading="lazy" />
        </button>

        <button
          type="button"
          className={`pay-type-btn ${cardType === "mc" ? "active" : ""}`}
          onClick={() => setCardType("mc")}
          disabled={payLoading}
          aria-label="Mastercard"
        >
          <img src={mc} alt="Mastercard" loading="lazy" />
        </button>

        <button
          type="button"
          className={`pay-type-btn ${cardType === "amex" ? "active" : ""}`}
          onClick={() => setCardType("amex")}
          disabled={payLoading}
          aria-label="American Express"
        >
          <img src={amex} alt="American Express" loading="lazy" />
        </button>
      </div>

      {/* ✅ preview tarjeta */}
      <div className="pay-card-preview" aria-hidden="true">
        <div className="pay-card-top">
          <img src={chipImg} className="pay-chip-img" alt="" />
          <img
            src={cardType === "visa" ? visa : cardType === "mc" ? mc : amex}
            className="pay-type-logo"
            alt=""
          />
        </div>

        <div className="pay-card-num">
          {cardNumber ? formatCardNumber(cardNumber) : "0000 0000 0000 0000"}
        </div>

        <div className="pay-card-row">
          <div className="pay-card-name">{cardHolder ? cardHolder : "NOMBRE TITULAR"}</div>
          <div className="pay-card-exp">
            {(cardExpMonth || "MM")}/{(cardExpYear || "YY")}
          </div>
        </div>
      </div>

      {payError && <div className="pay-msg pay-error">⚠ {payError}</div>}
      {paySuccess && <div className="pay-msg pay-ok">✔ Fuente de pago actualizada</div>}

      <div className="pay-form">
        <label className="pay-label">
          Nombre y apellido del titular
          <input
            className="pay-input"
            value={cardHolder}
            onChange={(e) => setCardHolder(normalizeCardHolder(e.target.value))}
            placeholder="Tu nombre"
            autoFocus
          />
        </label>

        <label className="pay-label">
          Número de tarjeta
          <input
            className="pay-input"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            inputMode="numeric"
            placeholder="4242 4242 4242 4242"
            maxLength={19}
          />
        </label>

        <div className="pay-grid">
          <label className="pay-label">
            Mes
            <input
              className="pay-input"
              value={cardExpMonth}
              onChange={(e) => setCardExpMonth(onlyDigits(e.target.value).slice(0, 2))}
              inputMode="numeric"
              placeholder="MM"
              maxLength={2}
            />
          </label>

          <label className="pay-label">
            Año
            <input
              className="pay-input"
              value={cardExpYear}
              onChange={(e) => setCardExpYear(onlyDigits(e.target.value).slice(0, 2))}
              inputMode="numeric"
              placeholder="YY"
              maxLength={2}
            />
          </label>

          <label className="pay-label">
            CVC
            <input
              className="pay-input"
              value={cardCvc}
              onChange={(e) => setCardCvc(onlyDigits(e.target.value).slice(0, 4))}
              inputMode="numeric"
              placeholder="CVV"
              maxLength={4}
            />
          </label>
        </div>

        <div className="pay-actions">
          <button
            type="button"
            className="pay-cancel"
            onClick={() => !payLoading && setOpenPaymentModal(false)}
            disabled={payLoading}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="pay-save"
            onClick={handleSavePaymentSource}
            disabled={payLoading}
          >
            {payLoading ? "Guardando..." : "Guardar tarjeta"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
{openVideoGuide && (
  <div
    className="video-guide-overlay"
    onClick={(e) => {
      if (e.target === e.currentTarget) setOpenVideoGuide(false);
    }}
  >
    <div className="video-guide-modal" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="video-guide-close"
        onClick={() => setOpenVideoGuide(false)}
        aria-label="Cerrar video guiado"
      >
        <X size={18} />
      </button>

      <h3 className="video-guide-title">Video guiado</h3>

      <p className="video-guide-subtitle">
        {isMobileVideo
          ? "Conoce cómo moverte en la plataforma."
          : "Conoce cómo moverte en la plataforma."}
      </p>

      <div className="video-guide-box">
  <video
    key={isMobileVideo ? "cel" : "pc"}
    controls
    autoPlay
    playsInline
    preload="metadata"
    className="video-guide-player"
  >
    <source
      src={isMobileVideo ? "/videos/videocel.mp4" : "/videos/videopc.mp4"}
      type="video/mp4"
    />
    Tu navegador no soporta video.
  </video>
</div>
    </div>
  </div>
)}
    </>
  );
}
